import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { construirReciboHtml, construirReciboTexto } from '../utils/reciboHtml';

const LIMIT = 20;

const Pagos: React.FC = () => {
  const usuario = JSON.parse(localStorage.getItem('usuario') || '{}');
  const puedeAnular = ['gerente', 'administrador', 'cobrador'].includes(usuario.rol);
  const [pagos, setPagos] = useState<any[]>([]);
  const [facturasCliente, setFacturasCliente] = useState<any[]>([]);
  const [cargandoFacturasCliente, setCargandoFacturasCliente] = useState(false);
  const [stats, setStats] = useState({ total: 0, cobradoHoy: 0, cobradoTotal: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPagos, setTotalPagos] = useState(0);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ clienteId: '', facturaId: '', monto: '', metodo: 'efectivo', notas: '' });
  const [clienteSeleccionado, setClienteSeleccionado] = useState<any>(null);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [resultadosBusqueda, setResultadosBusqueda] = useState<any[]>([]);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [pagoDetalle, setPagoDetalle] = useState<any>(null);
  const busquedaRef = useRef<HTMLDivElement>(null);
  const now = new Date();
  const [filtromes, setFiltromes] = useState<number>(now.getMonth() + 1);
  const [filtroAnio, setFiltroAnio] = useState<number>(now.getFullYear());

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await api.get('/pagos', { params: { page, limit: LIMIT, mes: filtromes, anio: filtroAnio } });
      setPagos(res.data.data);
      setTotalPages(res.data.totalPages);
      setTotalPagos(res.data.total);
    } catch {}
    finally { setLoading(false); }
  };

  const cargarStats = async () => {
    try { setStats((await api.get('/pagos/stats', { params: { mes: filtromes, anio: filtroAnio } })).data); } catch {}
  };

  useEffect(() => { cargar(); cargarStats(); }, [page, filtromes, filtroAnio]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (busquedaRef.current && !busquedaRef.current.contains(e.target as Node)) {
        setDropdownVisible(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!busquedaCliente.trim()) { setResultadosBusqueda([]); setDropdownVisible(false); return; }
    const t = setTimeout(async () => {
      setBuscandoCliente(true);
      try {
        const res = await api.get('/clientes', { params: { q: busquedaCliente, limit: 8 } });
        const lista = res.data.data ?? res.data;
        setResultadosBusqueda(Array.isArray(lista) ? lista : []);
        setDropdownVisible(true);
      } catch {}
      finally { setBuscandoCliente(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [busquedaCliente]);

  const cambiarPeriodo = (mes: number, anio: number) => {
    setPage(1); setFiltromes(mes); setFiltroAnio(anio);
  };

  const deudaFactura = (f: any) => Math.max(0, Number(f.monto) - Number(f.montoPagado || 0));

  // Normaliza un telefono peruano de 9 digitos a formato wa.me (prefijo 51).
  // Devuelve null si el cliente no tiene telefono registrado.
  const numeroWhatsApp = (p: any): string | null => {
    const raw = (p.cliente?.telefono || p.cliente?.telefono2 || '').replace(/\D/g, '');
    if (!raw) return null;
    return raw.length === 9 ? '51' + raw : raw;
  };

  const imprimirRecibo = (p: any) => {
    const numero = numeroWhatsApp(p);
    const waUrl = numero
      ? `https://wa.me/${numero}?text=${encodeURIComponent(construirReciboTexto(p))}`
      : undefined;
    if (!numero) alert('El cliente no tiene teléfono registrado: se imprimirá el recibo sin el botón de WhatsApp.');
    const win = window.open('', '_blank', 'width=440,height=680');
    if (!win) return;
    win.document.write(construirReciboHtml(p, waUrl));
    win.document.close();
  };

  const compartirWhatsApp = (p: any) => {
    const numero = numeroWhatsApp(p);
    if (!numero) { alert('El cliente no tiene teléfono registrado'); return; }
    window.open(`https://wa.me/${numero}?text=${encodeURIComponent(construirReciboTexto(p))}`, '_blank');
  };

  const seleccionarCliente = async (cliente: any) => {
    setClienteSeleccionado(cliente);
    setBusquedaCliente(`${cliente.nombres} ${cliente.apellidos} — ${cliente.codigo}`);
    setDropdownVisible(false);
    setResultadosBusqueda([]);
    setForm(f => ({ ...f, clienteId: cliente.id, facturaId: '', monto: '' }));
    setFacturasCliente([]);
    setCargandoFacturasCliente(true);
    try {
      const res = await api.get(`/facturas/cliente/${cliente.id}`);
      const pendientes = (res.data as any[]).filter(f =>
        (f.estado === 'pendiente' || f.estado === 'vencido') && deudaFactura(f) > 0
      );
      setFacturasCliente(pendientes);
      if (pendientes[0]) {
        setForm(f => ({ ...f, facturaId: pendientes[0].id, monto: String(deudaFactura(pendientes[0]).toFixed(2)) }));
      }
    } catch {}
    finally { setCargandoFacturasCliente(false); }
  };

  const abrirModal = () => {
    setModal(true);
    setClienteSeleccionado(null);
    setBusquedaCliente('');
    setResultadosBusqueda([]);
    setFacturasCliente([]);
    setForm({ clienteId: '', facturaId: '', monto: '', metodo: 'efectivo', notas: '' });
  };

  const anularPago = async (id: string) => {
    if (!window.confirm('¿Anular este pago? Se revertirá su efecto sobre la factura.')) return;
    try {
      await api.put(`/pagos/${id}/anular`);
      cargar(); cargarStats();
    } catch { alert('Error al anular el pago'); }
  };

  const registrar = async () => {
    if (!form.clienteId || !form.monto) { alert('Completa los campos requeridos'); return; }
    try {
      await api.post('/pagos', form);
      setModal(false);
      setForm({ clienteId: '', facturaId: '', monto: '', metodo: 'efectivo', notas: '' });
      setFacturasCliente([]);
      cargar(); cargarStats();
    } catch { alert('Error al registrar pago'); }
  };

  const exportarCSV = async () => {
    try {
      const res = await api.get('/pagos', { params: { mes: filtromes, anio: filtroAnio, limit: 5000 } });
      const todos = res.data.data as any[];
      const rows = [
        ['Fecha', 'Cliente', 'DNI', 'Monto', 'Metodo', 'Cobrador', 'Factura'],
        ...todos.map(p => [
          new Date(p.createdAt).toLocaleString('es-PE'),
          `${p.cliente?.nombres || ''} ${p.cliente?.apellidos || ''}`.trim(),
          p.cliente?.dni || '',
          Number(p.monto).toFixed(2),
          p.metodo,
          `${p.cobrador?.nombre || ''} ${p.cobrador?.apellido || ''}`.trim(),
          p.factura?.numero || '',
        ])
      ];
      const csv = '﻿' + rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `pagos-${filtromes}-${filtroAnio}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Error al exportar'); }
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h2 style={{ color:'#f1f5f9', margin:0, fontSize:24, fontWeight:800 }}>💰 Pagos</h2>
          <p style={{ color:'#64748b', margin:'4px 0 0', fontSize:13 }}>Registro de pagos recibidos</p>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <select value={filtromes} onChange={e => cambiarPeriodo(Number(e.target.value), filtroAnio)}
            style={{ background:'#0f172a', border:'1px solid #334155', color:'#94a3b8', padding:'8px 10px', borderRadius:8, fontSize:13, cursor:'pointer' }}>
            {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((m, i) => (
              <option key={i+1} value={i+1}>{m}</option>
            ))}
          </select>
          <select value={filtroAnio} onChange={e => cambiarPeriodo(filtromes, Number(e.target.value))}
            style={{ background:'#0f172a', border:'1px solid #334155', color:'#94a3b8', padding:'8px 10px', borderRadius:8, fontSize:13, cursor:'pointer' }}>
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button onClick={async () => {
            try {
              const mes = String(filtromes).padStart(2, '0');
              const res = await api.get(`/reportes/pagos/excel?mes=${filtromes}&anio=${filtroAnio}`, { responseType: 'blob' });
              const href = URL.createObjectURL(res.data);
              const a = document.createElement('a');
              a.href = href;
              a.download = `pagos-${mes}-${filtroAnio}.xlsx`;
              a.click();
              URL.revokeObjectURL(href);
            } catch { alert('Error al generar Excel'); }
          }} style={{ background:'#0f172a', border:'1px solid #334155', color:'#94a3b8', padding:'10px 16px', borderRadius:10, cursor:'pointer', fontWeight:600, fontSize:13 }}>
            ⬇ Excel Pagos
          </button>
          <button onClick={exportarCSV}
            style={{ background:'#0f172a', border:'1px solid #334155', color:'#94a3b8', padding:'10px 16px', borderRadius:10, cursor:'pointer', fontWeight:600, fontSize:13 }}>
            📥 Exportar CSV
          </button>
          <button onClick={abrirModal}
            style={{ background:'#22c55e', border:'none', color:'white', padding:'10px 20px', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:14 }}>
            + Registrar Pago
          </button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'Total pagos', value: stats.total, color:'#38bdf8' },
          { label:'Cobrado hoy', value: `S/. ${stats.cobradoHoy.toFixed(2)}`, color:'#22c55e' },
          { label:'Cobrado del mes', value: `S/. ${stats.cobradoTotal.toFixed(2)}`, color:'#a78bfa' },
        ].map(s => (
          <div key={s.label} style={{ background:'#1e293b', borderRadius:10, padding:'14px 18px', border:`1px solid ${s.color}33` }}>
            <div style={{ color:'#64748b', fontSize:12 }}>{s.label}</div>
            <div style={{ color:s.color, fontSize:22, fontWeight:800 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {loading ? <div style={{ color:'#64748b', textAlign:'center', padding:48 }}>Cargando...</div> :
        <div style={{ background:'#1e293b', borderRadius:12, overflow:'hidden', border:'1px solid #334155' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#0f172a' }}>
                {['Cliente','Monto','Método','Cobrador','Fecha','Comprobante','Acción'].map(h => (
                  <th key={h} style={{ padding:'12px 16px', color:'#64748b', fontSize:12, fontWeight:600, textAlign:'left', borderBottom:'1px solid #334155' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagos.map((p, i) => (
                <tr key={p.id} style={{ borderBottom:'1px solid #0f172a', background: i % 2 === 0 ? 'transparent' : '#0f172a11' }}>
                  <td style={{ padding:'12px 16px', color:'#f1f5f9', fontWeight:600 }}>{p.cliente?.nombres} {p.cliente?.apellidos}</td>
                  <td style={{ padding:'12px 16px', color:'#22c55e', fontWeight:700, fontSize:15 }}>S/. {Number(p.monto).toFixed(2)}</td>
                  <td style={{ padding:'12px 16px' }}>
                    <span style={{ background:'#0ea5e922', color:'#38bdf8', border:'1px solid #0ea5e944', borderRadius:20, padding:'3px 10px', fontSize:12 }}>
                      {p.metodo}
                    </span>
                  </td>
                  <td style={{ padding:'12px 16px', color:'#94a3b8', fontSize:13 }}>{p.cobrador?.nombre} {p.cobrador?.apellido}</td>
                  <td style={{ padding:'12px 16px', color:'#64748b', fontSize:13 }}>
                    {new Date(p.createdAt).toLocaleString('es-PE', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                  </td>
                  <td style={{ padding:'12px 16px' }}>
                    <button onClick={() => setPagoDetalle(p)}
                      style={{ background:'#0ea5e922', border:'1px solid #0ea5e944', color:'#38bdf8', borderRadius:8, padding:'4px 12px', fontSize:12, cursor:'pointer', fontWeight:600 }}>
                      {(p.fotoVoucher || p.firma) ? '🧾 Ver' : '🖨 Recibo'}
                    </button>
                  </td>
                  <td style={{ padding:'12px 16px' }}>
                    {p.estado === 'anulado' ? (
                      <span style={{ background:'#ef444422', color:'#ef4444', border:'1px solid #ef444444', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700 }}>Anulado</span>
                    ) : puedeAnular ? (
                      <button onClick={() => anularPago(p.id)}
                        style={{ background:'#ef444422', border:'1px solid #ef444444', color:'#ef4444', borderRadius:8, padding:'4px 12px', fontSize:12, cursor:'pointer', fontWeight:600 }}>
                        Anular
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pagos.length === 0 && <div style={{ textAlign:'center', color:'#64748b', padding:32 }}>No hay pagos registrados</div>}
        </div>
      }

      {!loading && totalPagos > 0 && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:16 }}>
          <span style={{ color:'#64748b', fontSize:12 }}>
            Mostrando {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, totalPagos)} de {totalPagos} pagos
          </span>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              style={{ background:'#1e293b', border:'1px solid #334155', color: page <= 1 ? '#475569' : '#94a3b8', borderRadius:8, padding:'6px 14px', cursor: page <= 1 ? 'default' : 'pointer', fontSize:12, fontWeight:600 }}>
              ← Anterior
            </button>
            <span style={{ color:'#94a3b8', fontSize:12 }}>Página {page} de {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              style={{ background:'#1e293b', border:'1px solid #334155', color: page >= totalPages ? '#475569' : '#94a3b8', borderRadius:8, padding:'6px 14px', cursor: page >= totalPages ? 'default' : 'pointer', fontSize:12, fontWeight:600 }}>
              Siguiente →
            </button>
          </div>
        </div>
      )}

      {/* Modal voucher / comprobante */}
      {pagoDetalle && (
        <div onClick={() => setPagoDetalle(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#1e293b', borderRadius:16, padding:28, width:520, maxHeight:'90vh', overflowY:'auto', border:'1px solid #334155' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 style={{ color:'#f1f5f9', margin:0 }}>🧾 Comprobante de pago</h3>
              <button onClick={() => setPagoDetalle(null)} style={{ background:'transparent', border:'none', color:'#64748b', fontSize:22, cursor:'pointer' }}>✕</button>
            </div>

            {/* Info del pago */}
            <div style={{ background:'#0f172a', borderRadius:10, padding:14, marginBottom:16, display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              <div><div style={{ color:'#64748b', fontSize:11 }}>Cliente</div><div style={{ color:'#f1f5f9', fontWeight:600 }}>{pagoDetalle.cliente?.nombres} {pagoDetalle.cliente?.apellidos}</div></div>
              <div><div style={{ color:'#64748b', fontSize:11 }}>Monto</div><div style={{ color:'#22c55e', fontWeight:700, fontSize:16 }}>S/. {Number(pagoDetalle.monto).toFixed(2)}</div></div>
              <div><div style={{ color:'#64748b', fontSize:11 }}>Método</div><div style={{ color:'#38bdf8' }}>{pagoDetalle.metodo}</div></div>
              <div><div style={{ color:'#64748b', fontSize:11 }}>Fecha</div><div style={{ color:'#94a3b8', fontSize:12 }}>{new Date(pagoDetalle.createdAt).toLocaleString('es-PE')}</div></div>
              {pagoDetalle.notas && <div style={{ gridColumn:'1/-1' }}><div style={{ color:'#64748b', fontSize:11 }}>Notas</div><div style={{ color:'#94a3b8' }}>{pagoDetalle.notas}</div></div>}
            </div>

            {/* Foto voucher */}
            {pagoDetalle.fotoVoucher && (
              <div style={{ marginBottom:16 }}>
                <div style={{ color:'#94a3b8', fontSize:12, fontWeight:600, marginBottom:8 }}>📸 Foto del voucher / comprobante</div>
                <img src={pagoDetalle.fotoVoucher} alt="Voucher" style={{ width:'100%', borderRadius:10, border:'1px solid #334155' }} />
                <a href={pagoDetalle.fotoVoucher} download={`voucher-${pagoDetalle.id}.jpg`}
                  style={{ display:'block', marginTop:8, textAlign:'center', color:'#38bdf8', fontSize:12 }}>
                  ⬇ Descargar imagen
                </a>
              </div>
            )}

            {/* Firma */}
            {pagoDetalle.firma && (
              <div style={{ marginBottom:16 }}>
                <div style={{ color:'#94a3b8', fontSize:12, fontWeight:600, marginBottom:8 }}>✍️ Firma del cliente</div>
                <div style={{ background:'white', borderRadius:10, padding:8, border:'1px solid #334155' }}>
                  <img src={pagoDetalle.firma} alt="Firma" style={{ width:'100%', maxHeight:160, objectFit:'contain' }} />
                </div>
              </div>
            )}

            {/* Acciones */}
            <div style={{ display:'flex', gap:10, marginTop:8 }}>
              <button onClick={() => imprimirRecibo(pagoDetalle)}
                style={{ flex:1, background:'#1e3a5f', border:'1px solid #1e4080', color:'#60a5fa', borderRadius:8, padding:'10px', cursor:'pointer', fontWeight:700, fontSize:13 }}>
                🖨 Imprimir
              </button>
              <button onClick={() => compartirWhatsApp(pagoDetalle)}
                style={{ flex:1, background:'#14532d', border:'1px solid #166534', color:'#4ade80', borderRadius:8, padding:'10px', cursor:'pointer', fontWeight:700, fontSize:13 }}>
                📱 WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <div onClick={() => setModal(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#1e293b', borderRadius:16, padding:28, width:460, border:'1px solid #334155', maxHeight:'90vh', overflowY:'auto' }}>
            <h3 style={{ color:'#f1f5f9', margin:'0 0 20px' }}>💰 Registrar Pago</h3>

            {/* Buscador de cliente */}
            <div style={{ marginBottom:14 }} ref={busquedaRef}>
              <label style={{ color:'#94a3b8', fontSize:12, fontWeight:600, display:'block', marginBottom:6 }}>
                Buscar cliente
                {buscandoCliente && <span style={{ color:'#38bdf8', fontSize:11, marginLeft:8, fontWeight:400 }}>Buscando...</span>}
              </label>
              <div style={{ position:'relative' }}>
                <input
                  value={busquedaCliente}
                  onChange={e => { setBusquedaCliente(e.target.value); setClienteSeleccionado(null); setForm(f => ({ ...f, clienteId: '' })); }}
                  onFocus={() => { if (resultadosBusqueda.length > 0) setDropdownVisible(true); }}
                  placeholder="Escribe nombre, apellido, DNI o código..."
                  style={{ width:'100%', background:'#0f172a', border:`1px solid ${clienteSeleccionado ? '#22c55e' : '#334155'}`, borderRadius:8, padding:'10px 12px', color:'white', fontSize:13, boxSizing:'border-box' }}
                />
                {dropdownVisible && resultadosBusqueda.length > 0 && (
                  <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'#0f172a', border:'1px solid #334155', borderRadius:10, zIndex:100, overflow:'hidden', boxShadow:'0 8px 24px rgba(0,0,0,0.5)' }}>
                    {resultadosBusqueda.map(c => (
                      <div key={c.id} onMouseDown={() => seleccionarCliente(c)}
                        style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid #1e293b', display:'flex', justifyContent:'space-between', alignItems:'center' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#1e293b')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <div>
                          <div style={{ color:'#f1f5f9', fontWeight:600, fontSize:13 }}>{c.apellidos}, {c.nombres}</div>
                          <div style={{ color:'#64748b', fontSize:11, marginTop:2 }}>{c.direccion || 'Sin dirección'}</div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0, marginLeft:12 }}>
                          <div style={{ color:'#38bdf8', fontSize:12, fontWeight:700 }}>{c.codigo}</div>
                          {(c.mesesDeuda ?? 0) > 0 && (
                            <div style={{ color:'#ef4444', fontSize:10, marginTop:2 }}>{c.mesesDeuda} mes{c.mesesDeuda !== 1 ? 'es' : ''} deuda</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Tarjeta del cliente seleccionado */}
            {clienteSeleccionado && (
              <div style={{ background:'#0f172a', border:'1px solid #22c55e44', borderRadius:10, padding:'12px 14px', marginBottom:14 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                  <div>
                    <div style={{ color:'#f1f5f9', fontWeight:700, fontSize:14 }}>{clienteSeleccionado.nombres} {clienteSeleccionado.apellidos}</div>
                    <div style={{ color:'#64748b', fontSize:12, marginTop:2 }}>{clienteSeleccionado.direccion || '—'}</div>
                    <div style={{ color:'#64748b', fontSize:12 }}>Tel: {clienteSeleccionado.telefono || '—'}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ color:'#38bdf8', fontWeight:700, fontSize:13 }}>{clienteSeleccionado.codigo}</div>
                    <div style={{ color:'#22c55e', fontSize:11, marginTop:4 }}>✓ Seleccionado</div>
                  </div>
                </div>
                {cargandoFacturasCliente && (
                  <div style={{ color:'#64748b', fontSize:12, marginTop:8 }}>Cargando facturas pendientes...</div>
                )}
                {!cargandoFacturasCliente && facturasCliente.length === 0 && form.clienteId && (
                  <div style={{ color:'#22c55e', fontSize:12, marginTop:8, fontWeight:600 }}>✓ Sin facturas pendientes</div>
                )}
              </div>
            )}

            {/* Facturas pendientes del cliente */}
            {facturasCliente.length > 0 && (
              <div style={{ marginBottom:14 }}>
                <label style={{ color:'#94a3b8', fontSize:12, fontWeight:600, display:'block', marginBottom:6 }}>
                  Facturas pendientes ({facturasCliente.length})
                </label>
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {facturasCliente.map(f => (
                    <div key={f.id} onClick={() => setForm(prev => ({ ...prev, facturaId: f.id, monto: String(deudaFactura(f).toFixed(2)) }))}
                      style={{ background: form.facturaId === f.id ? '#22c55e1a' : '#0f172a', border:`1px solid ${form.facturaId === f.id ? '#22c55e66' : '#334155'}`, borderRadius:8, padding:'10px 12px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', transition:'all 0.15s' }}>
                      <div>
                        <div style={{ color:'#f1f5f9', fontWeight:600, fontSize:13 }}>{f.numero}</div>
                        <div style={{ color:'#64748b', fontSize:11 }}>Vence: {f.fechaVencimiento ? new Date(f.fechaVencimiento).toLocaleDateString('es-PE') : '—'}</div>
                        <span style={{ background: f.estado === 'vencido' ? '#ef444422' : '#f59e0b22', color: f.estado === 'vencido' ? '#ef4444' : '#f59e0b', fontSize:10, padding:'1px 6px', borderRadius:10, fontWeight:700 }}>{f.estado}</span>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ color: form.facturaId === f.id ? '#22c55e' : '#f59e0b', fontWeight:800, fontSize:16 }}>S/. {deudaFactura(f).toFixed(2)}</div>
                        {Number(f.montoPagado) > 0 && <div style={{ color:'#64748b', fontSize:10 }}>Total: S/. {Number(f.monto).toFixed(2)}</div>}
                        {form.facturaId === f.id && <div style={{ color:'#22c55e', fontSize:10 }}>✓ Seleccionada</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom:14 }}>
              <label style={{ color:'#94a3b8', fontSize:12, fontWeight:600, display:'block', marginBottom:6 }}>Monto a cobrar (S/.)</label>
              <input value={form.monto} onChange={e => setForm({...form, monto: e.target.value})} type="number"
                style={{ width:'100%', background:'#0f172a', border:'1px solid #334155', borderRadius:8, padding:'9px 12px', color:'white', fontSize:15, fontWeight:700, boxSizing:'border-box' }} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ color:'#94a3b8', fontSize:12, fontWeight:600, display:'block', marginBottom:6 }}>Método de pago</label>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
                {[
                  { value:'efectivo', icon:'💵', label:'Efectivo' },
                  { value:'yape',     icon:'📱', label:'Yape' },
                  { value:'plin',     icon:'💜', label:'Plin' },
                  { value:'transferencia', icon:'🏦', label:'Transf.' },
                ].map(m => (
                  <button key={m.value} type="button" onClick={() => setForm(f => ({ ...f, metodo: m.value }))}
                    style={{ background: form.metodo === m.value ? '#0ea5e9' : '#0f172a', border:`1px solid ${form.metodo === m.value ? '#0ea5e9' : '#334155'}`, borderRadius:8, padding:'10px 4px', cursor:'pointer', color: form.metodo === m.value ? 'white' : '#94a3b8', fontSize:11, fontWeight:700, display:'flex', flexDirection:'column', alignItems:'center', gap:4, transition:'all 0.15s' }}>
                    <span style={{ fontSize:18 }}>{m.icon}</span>{m.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ color:'#94a3b8', fontSize:12, fontWeight:600, display:'block', marginBottom:6 }}>Notas (opcional)</label>
              <input value={form.notas} onChange={e => setForm({...form, notas: e.target.value})}
                style={{ width:'100%', background:'#0f172a', border:'1px solid #334155', borderRadius:8, padding:'9px 12px', color:'white', fontSize:13, boxSizing:'border-box' }} />
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setModal(false)} style={{ flex:1, background:'#334155', border:'none', color:'#94a3b8', borderRadius:8, padding:'10px', cursor:'pointer', fontWeight:600 }}>Cancelar</button>
              <button onClick={registrar} disabled={!form.clienteId || !form.monto}
                style={{ flex:2, background: form.clienteId && form.monto ? '#22c55e' : '#334155', border:'none', color: form.clienteId && form.monto ? 'white' : '#475569', borderRadius:8, padding:'10px', cursor: form.clienteId && form.monto ? 'pointer' : 'default', fontWeight:700, fontSize:15, transition:'all 0.15s' }}>
                {form.clienteId && form.monto ? `✓ Registrar S/. ${Number(form.monto).toFixed(2)}` : 'Registrar Pago'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pagos;
